import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuthStore } from '@/app/store/authStore';
import { useSettingsStore } from '@/app/store/settingsStore';
import { getStorageUrl } from '@/config/env';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { OcrGlobalStatus } from '@/components/digitization/OcrGlobalStatus';
import { LogOut, User, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useMatches, Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { sounds } from '@/utils/soundEffects';

export function Topbar() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const getSetting = useSettingsStore((s) => s.getSetting);
  const enableProfileView = getSetting('enable_profile_view', true);

  const handleLogout = () => {
    sounds.playSignOut();
    setShowLogoutDialog(false);
    logout();
  };

  const matches = useMatches() as Array<{ pathname: string; handle?: { crumb?: string } }>;
  const crumbs = matches.filter((m) => m.handle?.crumb);
  const hasCrumbs = crumbs.length > 0;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const greeting = getGreeting();
  const userFullName = ((user as any)?.name || `${(user as any)?.first_name || ''} ${(user as any)?.last_name || ''}`.trim() || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User');

  return (
    <header className="h-16 flex items-center justify-between px-6 bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-10 shadow-sm gap-4 sm:gap-6">
      <div className="flex-1 max-w-5xl flex items-center gap-3 min-w-0">
        <div className="flex-1 min-w-0">
          <GlobalSearch />
        </div>
        <OcrGlobalStatus />
      </div>
      
      <div className="flex items-center justify-end space-x-6 shrink-0">
        <NotificationBell />
        
        <div className="flex items-center space-x-4 border-l border-slate-200 pl-6">
          <div className="flex items-center space-x-3">
            <div className="flex items-center">
              <span className="text-sm font-semibold text-slate-800 hidden lg:block whitespace-nowrap">
                {greeting} 👋, {userFullName} <span className="text-slate-500 font-medium capitalize">({user?.roles?.[0]?.name?.replace('-', ' ') || 'User'})</span>
              </span>
            </div>
            <Link 
              to={enableProfileView ? "/profile" : "#"} 
              onClick={(e) => { if (!enableProfileView) e.preventDefault(); }}
              className="h-10 w-10 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center text-indigo-700 shadow-sm shrink-0 overflow-hidden transition-all hover:ring-2 hover:ring-indigo-300 cursor-pointer"
            >
              {(user as any)?.profile_photo_path ? (
                <img src={getStorageUrl((user as any).profile_photo_path)} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <User className="h-5 w-5" />
              )}
            </Link>
          </div>
          <button 
            onClick={() => {
              sounds.playClick();
              setShowLogoutDialog(true);
            }} 
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
            title="Sign Out"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirm Sign Out
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to sign out? You will need to enter your credentials to access the system again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 flex sm:justify-end gap-2">
            <Button variant="outline" onClick={() => {
              sounds.playClick();
              setShowLogoutDialog(false);
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              Sign Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
